// Public meditation detail page. URL: /m/{orderId}
// Renders one meditation from a publicly-shared order. Used as the canonical
// permalink for each meditation — same URL we'll publish to ATproto / share
// to Bluesky / link from cards on the homepage.
//
// Read-only. No auth. Anyone with the link can read.

import { useEffect, useState, useMemo } from "react";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { trackMeditationView } from "../../utils/analytics";
import SiteNav from "./components/SiteNav";
import SiteFooter from "./sections/SiteFooter";
import PanBar from "./components/PanBar";
import { meditationCaption, shortLabel } from "../../utils/meditations";

export default function MeditationDetail({ orderId, navigate }) {
  const [order, setOrder]   = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [pool, setPool]     = useState([]); // sibling list for prev/next

  useEffect(() => {
    if (!orderId) return;
    let cancel = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "orders", orderId));
        if (!cancel) {
          setOrder(snap.exists() ? { id: snap.id, ...snap.data() } : null);
          setLoaded(true);
          if (snap.exists()) trackMeditationView(orderId);
        }
      } catch {
        if (!cancel) setLoaded(true);
      }
    })();
    return () => { cancel = true; };
  }, [orderId]);

  // Subscribe to all public meditations so prev/next nav works.
  // Meditation text presence IS the publish signal — matches the cloud
  // function's new filter (buildPublicMeditations) so the nav pool lines
  // up with what's in config/publicMeditations.
  useEffect(() => {
    const q = query(collection(db, "orders"), where("state", "==", "done"));
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((o) => !o.hidden && !o.trashedAt && o.meditation?.trim() && (o.photo_cutout_url || o.photo_url))
        .sort((a, b) => {
          const ta = a.meditatedAt?.toMillis?.() ?? a.completedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0;
          const tb = b.meditatedAt?.toMillis?.() ?? b.completedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0;
          return tb - ta;
        });
      setPool(rows);
    });
    return unsub;
  }, []);

  const idx = useMemo(() => pool.findIndex((o) => o.id === orderId), [pool, orderId]);
  const prev = idx > 0 ? pool[idx - 1] : null;
  const next = idx >= 0 && idx < pool.length - 1 ? pool[idx + 1] : null;

  // Set page title for sharing / browser tab
  useEffect(() => {
    if (order && order.meditation) {
      const title = `${shortLabel(order)} — Meditations · The Joy of Pancakes`;
      document.title = title;
    }
    return () => { document.title = "The Joy of Pancakes"; };
  }, [order]);

  return (
    <div style={{ background: "#081a2a", minHeight: "100vh", color: "var(--jop-cream)", fontFamily: "var(--jop-display)" }}>
      <SiteNav />

      <main style={{ maxWidth: "780px", margin: "0 auto", padding: "5.5rem 1.5rem 4rem" }}>
        <BackLink onClick={() => navigate("/m")} />

        {!loaded && <p style={{ color: "rgba(255,218,173,0.5)", textAlign: "center", padding: "2rem" }}>Loading…</p>}

        {loaded && (!order || !order.meditation || order.hidden || order.trashedAt) && (
          <NotFound navigate={navigate} />
        )}

        {loaded && order && order.meditation && !order.hidden && !order.trashedAt && (
          <Article order={order} />
        )}

        {(prev || next) && order && (
          <PrevNextNav prev={prev} next={next} navigate={navigate} />
        )}
      </main>

      <SiteFooter />
    </div>
  );
}

function BackLink({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "transparent",
        color: "rgba(255,218,173,0.6)",
        border: "1px solid rgba(255,218,173,0.2)",
        borderRadius: "999px",
        padding: "0.4rem 0.95rem",
        fontSize: "0.78rem",
        fontFamily: "inherit",
        fontWeight: 600,
        cursor: "pointer",
        marginBottom: "1.6rem",
      }}
    >
      ← All meditations
    </button>
  );
}

function Article({ order }) {
  const img = order.photo_cutout_url || order.photo_url;
  const isCutout = !!order.photo_cutout_url;
  const date = formatDate(order.meditatedAt ?? order.completedAt ?? order.createdAt);
  const subject = shortLabel(order);

  return (
    <article style={{ display: "flex", flexDirection: "column", gap: "1.6rem" }}>
      {/* Hero image. For cutouts we drop `overflow: hidden` (the PNG is already
          transparent, and clipping would crop the drop-shadow at the tile
          edges — the exact harsh cutoff Dan flagged). A little padding gives
          the shadow room to breathe beyond the image's intrinsic bounds. */}
      {img && (
        <div style={{
          aspectRatio: "1 / 1",
          maxWidth: "585px",
          margin: "0 auto",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: isCutout ? "transparent" : "#000",
          borderRadius: isCutout ? 0 : "14px",
          overflow: isCutout ? "visible" : "hidden",
          padding: isCutout ? "1.2rem" : 0,
          boxSizing: "border-box",
        }}>
          <img
            src={img}
            alt={subject}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              filter: isCutout
                ? "drop-shadow(0 24px 48px rgba(0,0,0,0.65)) drop-shadow(0 6px 14px rgba(0,0,0,0.4))"
                : "none",
            }}
          />
        </div>
      )}

      <div style={{ textAlign: "center" }}>
        <PanBar width={64} height={3} margin="0 auto 1rem" />
        <h1 style={{
          margin: 0,
          fontFamily: "var(--jop-script)",
          fontSize: "clamp(1.9rem, 4.5vw, 2.6rem)",
          color: "var(--jop-cream)",
          fontWeight: 700,
          lineHeight: 1.15,
        }}>
          {subject}
        </h1>
        {date && (
          <div style={{
            marginTop: "0.55rem",
            color: "rgba(255,218,173,0.5)",
            fontSize: "0.78rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}>
            {date}{order.artistName ? ` · ${order.artistName}` : ""}
          </div>
        )}
      </div>

      {/* Meditation body */}
      <div style={{
        background: "rgba(252,179,31,0.04)",
        border: "1px solid rgba(252,179,31,0.18)",
        borderRadius: "14px",
        padding: "1.6rem 1.6rem 1.4rem",
      }}>
        <div style={{
          color: "var(--jop-gold)",
          fontSize: "0.65rem",
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          fontWeight: 700,
          marginBottom: "0.8rem",
        }}>
          🥞 From Doctor Dan
        </div>
        <div style={{
          color: "var(--jop-cream)",
          fontSize: "clamp(1rem, 2vw, 1.12rem)",
          lineHeight: 1.7,
          whiteSpace: "pre-wrap",
        }}>
          {order.meditation}
        </div>
      </div>

      {/* AI description as supplemental context */}
      {order.ai_description && (
        <div style={{ padding: "0 0.5rem" }}>
          <div style={{
            color: "rgba(255,218,173,0.4)",
            fontSize: "0.65rem",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            fontWeight: 700,
            marginBottom: "0.45rem",
          }}>
            About this pancake
          </div>
          <p style={{
            color: "rgba(255,218,173,0.75)",
            fontSize: "0.92rem",
            lineHeight: 1.65,
            margin: 0,
          }}>
            {order.ai_description}
          </p>
        </div>
      )}
    </article>
  );
}

function PrevNextNav({ prev, next, navigate }) {
  return (
    <div style={{
      marginTop: "3rem",
      paddingTop: "1.5rem",
      borderTop: "1px solid rgba(255,218,173,0.12)",
      display: "flex",
      justifyContent: "space-between",
      gap: "1rem",
      flexWrap: "wrap",
    }}>
      {prev ? (
        <button
          onClick={() => navigate(`/m/${prev.id}`)}
          style={navBtnStyle}
        >
          ← {meditationCaption(prev).slice(0, 36)}{meditationCaption(prev).length > 36 ? "…" : ""}
        </button>
      ) : <span />}
      {next ? (
        <button
          onClick={() => navigate(`/m/${next.id}`)}
          style={{ ...navBtnStyle, textAlign: "right" }}
        >
          {meditationCaption(next).slice(0, 36)}{meditationCaption(next).length > 36 ? "…" : ""} →
        </button>
      ) : <span />}
    </div>
  );
}

function NotFound({ navigate }) {
  return (
    <div style={{
      textAlign: "center",
      padding: "3rem 1rem",
      color: "rgba(255,218,173,0.6)",
      fontFamily: "inherit",
    }}>
      <p style={{ marginBottom: "1.2rem" }}>This meditation isn't published, or doesn't exist.</p>
      <button
        onClick={() => navigate("/")}
        style={navBtnStyle}
      >
        ← Back to The Joy of Pancakes
      </button>
    </div>
  );
}

const navBtnStyle = {
  background: "rgba(252,179,31,0.06)",
  color: "var(--jop-gold)",
  border: "1px solid rgba(252,179,31,0.3)",
  borderRadius: "10px",
  padding: "0.65rem 1rem",
  fontFamily: "inherit",
  fontSize: "0.85rem",
  fontWeight: 600,
  cursor: "pointer",
  flex: "1 1 auto",
  maxWidth: "48%",
};

function formatDate(ts) {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}
